import React from 'react'
import { ArtboardDetailInfoFragment, ShareInfoFragment } from 'gqlTypes'
import useUserSignedIn from 'modules/user/useUserSignedIn'
import { useVersioning } from 'modules/versioning'
import { NavigationContainer } from './HeaderNavigation.styles'
import { isArtboardRoute, useForTablet } from 'components/Breakpoint'
import { routes } from 'constants/routes'
import Navbar from 'components/Navbar'
import ProjectName from 'components/ProjectName'
import PagesDropdown from 'modules/shares/components/PagesDropdown'
import ArtboardsDropdown from 'modules/shares/components/ArtboardsDropdown'
import NavigationItem from './NavigationItem'
import { useRouteMatch } from 'react-router-dom'
import { Breadcrumbs } from './Breadcrumbs'
import { useFlag } from 'constants/flags'
import { CWV_ROUTES } from 'modules/shares/ComponentsWebView'

export type ShareView = 'document' | 'pageCanvas' | 'artboard'

export interface ShareForNavigation
  extends Pick<
    ShareInfoFragment,
    'project' | 'identifier' | 'name' | 'workspace'
  > {}

const getProjectLink = (share: ShareForNavigation) => {
  const { project, workspace } = share
  const { identifier } = workspace!

  if (!project) {
    return null
  }

  const projectName = <ProjectName project={project} />

  if (project?.type === 'PERSONAL_DRAFTS') {
    return {
      children: projectName,
      to: routes.WORKSPACE_DRAFTS.create({ workspaceId: identifier }),
    }
  }

  return {
    children: projectName,
    to: routes.WORKSPACE_PROJECT.create({
      projectId: project.shortId,
      workspaceId: identifier,
    }),
  }
}

interface HeaderNavigationBaseProps {
  share: ShareForNavigation
  /** Define the view currently active so we can display the breadcrumbs accordingly */
  currentView: ShareView
  pageCount: number
  isSignedIn: boolean
}

/**
 * Prop type is union of two types to force passing artboard prop
 * when currentView value is 'artboard'.
 */
export type HeaderNavigationProps =
  | (HeaderNavigationContainerArtboardViewProps & HeaderNavigationBaseProps)
  | (HeaderNavigationContainerOtherViewProps & HeaderNavigationBaseProps)

/**
 * Breadcrumbs shown in the header of the DocumentView, PageCanvasView & DetailView.
 */
export const HeaderNavigation: React.FC<HeaderNavigationProps> = ({
  share,
  currentView,
  artboard,
  isSignedIn,
  pageCount = 0,
}) => {
  const isComponentsWebViewOn = useFlag('components-web-view')
  const documentName = share.name
  const projectLink = getProjectLink(share)
  const isTabletAndBigger = useForTablet()
  const isSmallerThanTablet = !isTabletAndBigger
  const { path } = useRouteMatch()

  return (
    <NavigationContainer align={isTabletAndBigger ? 'center' : 'start'}>
      {isArtboardRoute(path) && isSmallerThanTablet ? (
        <Navbar.Title>{documentName}</Navbar.Title>
      ) : (
        share && (
          // They key makes sure that in case the document name changes,
          // the collapsed breadcrumbs will be re-calculated
          <Breadcrumbs key={documentName}>
            {/* Project */}
            {isSignedIn && projectLink && (
              <NavigationItem
                key="project"
                to={projectLink.to}
                data-testid="breadcrumb-project">
                {projectLink.children}
              </NavigationItem>
            )}

            {/* Document */}
            <NavigationItem
              key="document"
              to={routes.SHARE_VIEW.create({ shareID: share.identifier })}
              data-testid="breadcrumb-document">
              {documentName}
            </NavigationItem>

            {/* Page */}
            {getPageBreadcrumb(
              share,
              currentView,
              pageCount,
              path,
              isComponentsWebViewOn,
              artboard
            )}
            {/* Artboard */}
            {currentView === 'artboard' && artboard && (
              /* This component receives the prop "collapse" via "React.cloneElement" please be aware*/
              <ArtboardsDropdown
                key="artboard-dropdown"
                shareIdentifier={share.identifier}
                artboard={artboard}
              />
            )}
          </Breadcrumbs>
        )
      )}
    </NavigationContainer>
  )
}

interface HeaderNavigationContainerArtboardViewProps {
  currentView: 'artboard'
  // artboard needs to be passed but can be undefined when data is loading
  artboard: ArtboardDetailInfoFragment | undefined
}
interface HeaderNavigationContainerOtherViewProps {
  currentView: 'document' | 'pageCanvas'
  artboard?: undefined
}

/**
 * Prop type is union of two types to force passing artboard prop
 * when currentView value is 'artboard'.
 */
type HeaderNavigationContainerProps =
  | HeaderNavigationContainerArtboardViewProps
  | HeaderNavigationContainerOtherViewProps

const HeaderNavigationContainer = (props: HeaderNavigationContainerProps) => {
  const { share, ...versionContext } = useVersioning()!
  const isSignedIn = useUserSignedIn()

  const pageCount =
    (!versionContext.loading &&
      versionContext.currentVersion.document?.pageCount) ||
    0

  return (
    <HeaderNavigation
      {...props}
      isSignedIn={isSignedIn}
      share={share}
      pageCount={pageCount}
    />
  )
}

/**
 * Note: this needs to remain a function and can not be a PageBreadCrumb component because
 * <Breadcrumbs /> Parent component inject props using cloneElement that would be
 * added to the PageBreadCrumb component instead of the sub-components returned in this function.
 */
function getPageBreadcrumb(
  share: ShareForNavigation,
  currentView: ShareView,
  pageCount: number,
  path: string,
  isComponentsWebViewOn: boolean,
  artboard?: ArtboardDetailInfoFragment
) {
  const isCwvRoute = CWV_ROUTES.find((route: string) => route === path) ?? false

  const showPagesDropdown =
    (isComponentsWebViewOn && !isCwvRoute) || !isComponentsWebViewOn

  if (currentView === 'document' && pageCount > 1 && showPagesDropdown) {
    return (
      <PagesDropdown
        key="page-dropdown"
        pageLinkRouteKey="SHARE_PAGE_VIEW"
        shareIdentifier={share.identifier}
        showOptionAllPages={true}
      />
    )
  }

  if (currentView === 'pageCanvas') {
    return (
      <PagesDropdown
        key="page-dropdown"
        pageLinkRouteKey="SHARE_PAGE_CANVAS_VIEW"
        shareIdentifier={share.identifier}
        showOptionAllPages={false}
      />
    )
  }

  if (currentView === 'artboard' && pageCount > 1) {
    return (
      <NavigationItem
        key="breadcrumb-page"
        to={routes.SHARE_PAGE_VIEW.create({
          shareID: share.identifier,
          pageUUID: artboard?.page?.uuid!,
        })}
        data-testid="breadcrumb-page">
        {artboard?.page?.name}
      </NavigationItem>
    )
  }

  return null
}

export default HeaderNavigationContainer
